import { createContext, useContext } from 'react';

import { ApolloClient } from '@apollo/client';
import { createStore, StoreApi, useStore } from 'zustand';

import {
  GetProductsByIdBatchedQuery,
  WishlistForCurrentUserQuery,
  WishlistForCurrentUserQueryVariables,
  WishlistSortBy,
  WishlistSortByOption,
} from '@notino/shared/definitions/types';

import { WISHLIST_PAGE_SIZE } from './constants';
import wishlistForCurrentUser from './queries/wishlistForCurrentUser.graphql';

type WishlistState = {
  missingProductIds: number[];
  productIds: number[]; // navigation api might return null products, so we have this productIds to do pagination properly
  products: GetProductsByIdBatchedQuery['vpProductByIds'];
  hasMore: boolean;
  isLoading: boolean;
  totalProductCount: number;
  totalFilteredProductCount: number;
  isSharedWishlist: boolean;

  sortByOptions: WishlistSortByOption[];
  selectedSortBy: WishlistSortBy;

  filtration: WishlistForCurrentUserQuery['WishlistForCurrentUser']['filtration'];
  selectedNavigationValues: number[];

  selectionMode: boolean;
  selectedIds: string[];

  actions: {
    setProductWishlisted: (productId: string, wishlisted: boolean) => void;
    setSelectedSortBy: (sortBy: WishlistSortBy) => void;
    setSelectedNavigationValues: (values: number[]) => void;
    toggleSelectionMode: () => void;
    setProductSelected: (productId: string, selected: boolean) => void;
    bulkRemoveProducts: (productIds: string[]) => void;
    fetchProducts: (
      variables: Omit<WishlistForCurrentUserQueryVariables, 'count'>
    ) => Promise<void>;
  };
};

type WishlistStore = StoreApi<WishlistState>;

export const createWishlistStore = (
  initParams: Omit<
    WishlistState,
    | 'actions'
    | 'selectedSortBy'
    | 'selectedNavigationValues'
    | 'isLoading'
    | 'selectedIds'
  >,
  client: ApolloClient<object>,
  listIds: { listId: string; sharedListId: string }
) =>
  createStore<WishlistState>((set, get) => ({
    ...initParams,
    selectedSortBy: WishlistSortBy.Default,
    selectedNavigationValues: [],
    isLoading: false,
    selectedIds: [],
    actions: {
      setSelectedSortBy: (sortBy) => {
        void get().actions.fetchProducts({
          sortBy,
          offset: 0,
        });
        set({ selectedSortBy: sortBy, productIds: [], products: [] });
      },

      setSelectedNavigationValues: (values) => {
        void get().actions.fetchProducts({
          navigationValues: values,
          offset: 0,
        });
        set({ selectedNavigationValues: values, productIds: [], products: [] });
      },

      setProductWishlisted: (productId, wishlisted) => {
        if (get().isSharedWishlist) {
          set({
            products: get().products.map((p) =>
              p.id === String(productId) ? { ...p, wishlisted } : p
            ),
          });
          return;
        }
        set({
          products: get().products.filter((p) => p.id !== productId),
          productIds: get().productIds.filter((p) => p !== Number(productId)),
          totalFilteredProductCount: get().totalFilteredProductCount - 1,
          totalProductCount: get().totalProductCount - 1,
        });
      },

      toggleSelectionMode: () => {
        const newSelectionMode = !get().selectionMode;
        if (newSelectionMode === false) {
          set({ selectedIds: [] });
        }
        set({ selectionMode: newSelectionMode });
      },

      setProductSelected: (productId, isSelected) => {
        if (isSelected) {
          set({ selectedIds: [...get().selectedIds, productId] });
        } else {
          set({
            selectedIds: get().selectedIds.filter((id) => id !== productId),
          });
        }
      },

      bulkRemoveProducts: (productIds) => {
        set({
          productIds: get().productIds.filter(
            (id) => !productIds.includes(String(id))
          ),
          products: get().products.filter(
            (product) => !productIds.includes(String(product.id))
          ),
          totalProductCount: get().totalProductCount - productIds.length,
        });
      },

      fetchProducts: async ({ offset, navigationValues, sortBy }) => {
        set({ isLoading: true });
        const { data } = await client.query<
          WishlistForCurrentUserQuery,
          WishlistForCurrentUserQueryVariables
        >({
          query: wishlistForCurrentUser,
          fetchPolicy: 'no-cache',
          variables: {
            offset,
            sortBy: sortBy ?? get().selectedSortBy,
            navigationValues:
              navigationValues ?? get().selectedNavigationValues,
            count: WISHLIST_PAGE_SIZE,
            wishlist: {
              listId: listIds.listId,
              wishlistId: listIds.sharedListId,
            },
          },
        });

        const { hasMore, products, sortByOptions, totalProductCount } =
          data.WishlistForCurrentUser.listing;
        const { filtration } = data.WishlistForCurrentUser;

        set((prev) => ({
          hasMore,
          isLoading: false,
          sortByOptions,
          totalFilteredProductCount: totalProductCount,
          filtration,
          selectedNavigationValues: get().selectedNavigationValues.filter(
            (val) =>
              filtration.some((f) =>
                f.items.some((i) => i.navigationValue === val)
              )
          ),
          products: [
            ...prev.products,
            ...products.map((x) => x.product).filter(Boolean),
          ],
          productIds: [
            ...prev.productIds,
            ...products.map((x) => x.webProductId),
          ],
        }));
      },
    },
  }));

const Context = createContext<{ store: WishlistStore }>(null!);
export const WishlistContextProvider = Context.Provider;

export const useWishlistStore = <T>(
  selector: Parameters<typeof useStore<WishlistStore, T>>[1]
) => {
  const ctx = useContext(Context);
  if (!ctx) {
    throw new Error('useWishlistStore must be used inside WishlistProvider');
  }
  return useStore(ctx?.store, selector);
};
